import { Button } from "@/components/ui/button";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Form,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { MultiSelect } from "@/components/ui/multi-select";
import { Textarea } from "@/components/ui/textarea";
import { useUser, useUpdateProfileAudio } from "@/lib/api/user";
import { useUpdateUser } from "@/lib/hooks/user/use-update-user";
import { useSignedMedia } from "@/lib/hooks/use-signed-media";
import { MENTOR_INTEREST_OPTIONS } from "@/lib/mentor-profile/constants";
import {
  interestsOptions,
  struggledWithOptions,
  mostLikeYouOptions,
} from "@/lib/strings/profile-data";
import { DetailFieldMapping } from "../detail-field-mapping";
import { MatchCardPhotoSection } from "../match-card-photo-section";
import { AudioDialog } from "@/components/shared/audio-dialog";
import { MutingAudio } from "@/components/shared/muting-media";
import { useMemo, useState } from "react";
import { Loader2, Upload } from "lucide-react";
import { VideoSection } from "@/components/shared/video-section";
import {
  useCreateVideoMessage,
  useDeleteMessage,
  useMessages,
} from "@/lib/hooks/onboarding-messages/use-onboarding-messages";
import {
  OnboardingMessageType,
  MessageContext,
} from "@/lib/api/onboarding-messages";
import { InterestImagesManager } from "../interest-images/interest-images-manager";

interface ProfileSectionsManagerProps {
  userId: string;
}

export function DefaultProfileSections({
  userId,
}: ProfileSectionsManagerProps) {
  const { data: user, isPending } = useUser(userId);

  const { handleUpdate, form } = useUpdateUser({
    userId,
    profile: {
      favorite_music: user?.profile?.favorite_music || "",
      interests: user?.profile?.interests || [],
      life_experiences: user?.profile?.life_experiences || [],
      most_like_you: user?.profile?.most_like_you || [],
      favorite_food: user?.profile?.favorite_food || "",
    },
  });

  // Audio upload functionality
  const [isAudioDialogOpen, setIsAudioDialogOpen] = useState(false);
  const updateProfileAudio = useUpdateProfileAudio(userId);
  const { url: audioUrl } = useSignedMedia(
    user?.profile?.profile_audio_url ?? undefined,
    userId,
  );

  // State for interest details
  const [interestDetails, setInterestDetails] = useState<
    Record<string, string>
  >(user?.profile?.metadata?.interestDetails || {});

  // State for struggle details (single text field)
  const [struggleDetails, setStruggleDetails] = useState<string>(
    user?.profile?.metadata?.struggleDetails || "",
  );

  const handleInterestDetailsChange = (details: Record<string, string>) => {
    setInterestDetails(details);
    handleUpdate("profile", {
      ...form.getValues("profile"),
      metadata: {
        ...user?.profile?.metadata,
        interestDetails: details,
      },
    });
  };

  const handleStruggleDetailsChange = (value: string) => {
    setStruggleDetails(value);
    handleUpdate("profile", {
      ...form.getValues("profile"),
      metadata: {
        ...user?.profile?.metadata,
        struggleDetails: value,
      },
    });
  };

  const handleAudioUpload = async (file: File) => {
    await updateProfileAudio.mutateAsync(file);
  };

  const { data: messages = [] } = useMessages(userId, MessageContext.ONLINE);
  const fileMessage = useMemo(() => {
    return messages.find(
      (message: { type: OnboardingMessageType }) =>
        message.type === OnboardingMessageType.FILE,
    );
  }, [messages]);
  const deleteMessage = useDeleteMessage(userId, MessageContext.ONLINE);
  const createVideoMessage = useCreateVideoMessage(
    userId,
    MessageContext.ONLINE,
  );
  const { url: fileMessageSignedUrl } = useSignedMedia(fileMessage?.message);
  const handleDeleteMessage = (messageId: string) => {
    deleteMessage.mutate(messageId);
  };

  const selectedInterests = form.watch("profile.interests") ?? [];
  const interestSelectOptions = [
    ...interestsOptions,
    ...MENTOR_INTEREST_OPTIONS.filter(
      (label) => !interestsOptions.some((option) => option.value === label),
    ).map((label) => ({ label, value: label })),
  ];
  const interestImageInterests = [
    ...new Set([
      ...selectedInterests,
      ...(user?.profile?.interest_images ?? []).map((image) => image.interest),
    ]),
  ];

  if (isPending || !user) {
    return (
      <div className="flex items-center justify-center p-6">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Default profile sections</h3>
          <p className="text-sm text-muted-foreground">
            These sections are the same for all mentors
          </p>
        </div>
      </div>

      <div>
        <VideoSection
          title="Onboarding Video"
          videoUrl={fileMessageSignedUrl}
          onUpload={async (file) => {
            await createVideoMessage.mutateAsync({ file });
          }}
          onDelete={async () => {
            if (fileMessage?.id) {
              await handleDeleteMessage(fileMessage?.id);
            }
          }}
          isUploading={createVideoMessage.isPending}
          maxSize={256} // 256MB limit for onboarding videos
        />
      </div>

      {/* Two Column Layout */}
      <Form {...form}>
        <div className="flex gap-8">
          {/* Left Column */}
          <div className="flex-1 space-y-6">
            {/* Favourite song */}
            <FormField
              control={form.control}
              name="profile.favorite_music"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm font-medium">
                    Favourite song
                  </FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Spotify link"
                      {...field}
                      onBlur={() => {
                        handleUpdate("profile", {
                          ...form.getValues("profile"),
                          favorite_music: field.value,
                        });
                      }}
                    />
                  </FormControl>
                  <FormMessage className="text-xs" />
                </FormItem>
              )}
            />

            {/* Match Card Photo */}
            <MatchCardPhotoSection
              userId={userId}
              photoUrl={user?.profile?.match_card_photo_url}
            />

            {/* Audio message */}
            <FormItem>
              <FormLabel className="text-sm font-medium">
                Audio message
              </FormLabel>
              <div className="space-y-3">
                {audioUrl && (
                  <div className="rounded-lg border p-3">
                    <MutingAudio
                      src={audioUrl}
                      className="w-full"
                      controls
                      preload="metadata"
                    >
                      Your browser does not support the audio tag.
                    </MutingAudio>
                  </div>
                )}
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsAudioDialogOpen(true)}
                  className="w-full"
                >
                  <Upload className="mr-2 h-4 w-4" />
                  {audioUrl ? "Change Audio" : "Upload Audio"}
                </Button>
              </div>
            </FormItem>
          </div>

          {/* Right Column */}
          <div className="flex-1 space-y-6">
            {/* Interests */}
            <FormField
              control={form.control}
              name="profile.interests"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm font-medium">
                    Interests
                  </FormLabel>
                  <MultiSelect
                    options={interestSelectOptions}
                    defaultValue={
                      field?.value?.map((value) => ({
                        value,
                        label: value,
                      })) || []
                    }
                    onChange={(options) => {
                      const values = options.map((option) => option.value);

                      // Clean up interestDetails to only keep selected interests
                      const cleanedInterestDetails: Record<string, string> = {};
                      values.forEach((interest) => {
                        if (interestDetails[interest]) {
                          cleanedInterestDetails[interest] =
                            interestDetails[interest];
                        }
                      });

                      handleUpdate("profile", {
                        ...form.getValues("profile"),
                        interests: values,
                        metadata: {
                          ...user.profile?.metadata,
                          interestDetails: cleanedInterestDetails,
                        },
                      });
                      field.onChange(values);
                      setInterestDetails(cleanedInterestDetails);
                    }}
                    className="w-full"
                    placeholder="Select interests..."
                  />
                  <FormMessage className="text-xs" />
                </FormItem>
              )}
            />

            {/* Life experience */}
            <FormField
              control={form.control}
              name="profile.life_experiences"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm font-medium">
                    Life experience
                  </FormLabel>
                  <MultiSelect
                    options={struggledWithOptions}
                    defaultValue={
                      field?.value?.map((value) => ({
                        value,
                        label: value,
                      })) || []
                    }
                    onChange={(options) => {
                      const values = options.map((option) => option.value);
                      handleUpdate("profile", {
                        ...form.getValues("profile"),
                        life_experiences: values,
                      });
                      field.onChange(values);
                    }}
                    className="w-full"
                    placeholder="Select experiences..."
                  />
                  <FormMessage className="text-xs" />
                </FormItem>
              )}
            />

            {/* Most like you */}
            <FormField
              control={form.control}
              name="profile.most_like_you"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm font-medium">
                    Most like you
                  </FormLabel>
                  <MultiSelect
                    options={mostLikeYouOptions}
                    defaultValue={
                      field?.value?.map((value) => ({
                        value,
                        label: value,
                      })) || []
                    }
                    onChange={(options) => {
                      const values = options.map((option) => option.value);
                      handleUpdate("profile", {
                        ...form.getValues("profile"),
                        most_like_you: values,
                      });
                      field.onChange(values);
                    }}
                    className="w-full"
                    placeholder="Most like you"
                  />
                  <FormMessage className="text-xs" />
                </FormItem>
              )}
            />
          </div>
        </div>

        <div className="mt-6 space-y-4">
          <h3 className="text-lg font-semibold">Interest Images</h3>
          <p className="text-sm text-muted-foreground">
            Upload photos for each of this mentor&apos;s interests. These appear
            in the D73 mentor profile view (max 3 per interest).
          </p>
          <InterestImagesManager
            userId={userId}
            interests={interestImageInterests}
            interestImages={user.profile?.interest_images ?? []}
          />
        </div>

        {/* Divider with Centered Text */}
        <div className="relative mt-8">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-200"></div>
          </div>
          <div className="relative flex justify-center">
            <span className="bg-white px-4 text-sm font-medium text-gray-700">
              Old onboarding
            </span>
          </div>
        </div>

        {/* Interest Details - Full Width Below */}
        {(form.watch("profile.interests")?.length ?? 0) > 0 && (
          <div className="mt-6">
            <DetailFieldMapping
              title="Interest Details"
              selectedItems={form.watch("profile.interests") || []}
              details={interestDetails}
              onDetailsChange={handleInterestDetailsChange}
              placeholder="Tell us more about this interest..."
            />
          </div>
        )}

        {/* Experience Details - Full Width Below */}
        {(form.watch("profile.life_experiences")?.length ?? 0) > 0 && (
          <div className="mt-6">
            <FormItem>
              <FormLabel className="text-sm font-medium">
                Experience Details
              </FormLabel>
              <FormControl>
                <Textarea
                  value={struggleDetails}
                  onChange={(e) => setStruggleDetails(e.target.value)}
                  onBlur={(e) => handleStruggleDetailsChange(e.target.value)}
                  placeholder="Share more about your experiences..."
                  maxLength={1000}
                  className="min-h-[100px] resize-none"
                />
              </FormControl>
              <p className="mt-1 text-xs text-muted-foreground">
                {struggleDetails.length}/1000 characters
              </p>
            </FormItem>
          </div>
        )}

        {/* Favorite Food - Full Width Below */}
        <div className="mt-6">
          <FormField
            control={form.control}
            name="profile.favorite_food"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm font-medium">
                  Favorite Food
                </FormLabel>
                <FormControl>
                  <Input
                    placeholder="Enter favorite food"
                    {...field}
                    onBlur={() => {
                      handleUpdate("profile", {
                        ...form.getValues("profile"),
                        favorite_food: field.value,
                      });
                    }}
                  />
                </FormControl>
                <FormMessage className="text-xs" />
              </FormItem>
            )}
          />
        </div>

        {/* Audio Upload Dialog */}
        <AudioDialog
          open={isAudioDialogOpen}
          onClose={() => setIsAudioDialogOpen(false)}
          onUpload={handleAudioUpload}
          currentAudioUrl={audioUrl}
          title="Upload Audio Message"
          maxSize={10}
        />
      </Form>
    </div>
  );
}
